import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import numeral from 'numeral'

import { getCurrency } from '../../../../helpers/currencies'

import styles from './styles.module.scss'

import { PercentageIcon, ArrowIcon, AttentionIcon, PulseIcon } from '../icons'

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const PosControl = ({sales}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector((state) => state.company.companyInfo)

  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const { discounts, returns } = sales.reduce((acc, sale) => {
      return {
        ...acc,
        discounts: acc.discounts + (sale.DiscountSum || 0),
        returns: acc.returns + (sale.DishReturnSum || 0),
      }
    }, {
      discounts: 0,
      returns: 0,
    })
    const paymentMethodsMap = sales.reduce((acc, sale) => {
      return {
        ...acc,
        [sale.PayTypes]: acc[sale.PayTypes] ? [...acc[sale.PayTypes], sale] : [sale]
      }
    }, {})

    const totalSales = sales.reduce((acc, sale) => acc + sale.DishDiscountSumInt, 0)

    const paymentMethods = Object.entries(paymentMethodsMap).map(([method, sales]) => {
      return {
        method,
        amount: sales.reduce((acc, sale) => acc + sale.DishDiscountSumInt, 0),
        percentage: totalSales > 0 ? (sales.reduce((acc, sale) => acc + sale.DishDiscountSumInt, 0) / totalSales) * 100 : 0,
      }
    })

    return {
      discounts: discounts,
      returns: returns,
      cancellations: 0,
      manualCorrections: 0,
      paymentMethods: paymentMethods,
    }
  }, [sales])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('POS control')}</div>
      </div>
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <PercentageIcon width={12} height={12} />
            <div className={styles.cardTitle}>{t('Discounts')}</div>
          </div>
          <div className={styles.cardValue}>
            { formatNumber(data.discounts, currency.symbol) }
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <ArrowIcon />
            <div className={styles.cardTitle}>{t('Returns')}</div>
          </div>
          <div className={styles.cardValue}>
            { formatNumber(data.returns, currency.symbol) }
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <AttentionIcon width={12} height={12} />
            <div className={styles.cardTitle}>{t('Cancellations')}</div>
          </div>
          <div className={styles.cardValue}>
            { formatNumber(data.cancellations, currency.symbol) }
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <PulseIcon width={12} height={12} />
            <div className={styles.cardTitle}>{t('Manual corrections')}</div>
          </div>
          <div className={styles.cardValue}>
            { formatNumber(data.manualCorrections, currency.symbol) }
          </div>
        </div>
      </div>
      <div className={styles.sectionTitle}>
        { t('Payment methods') }
      </div>
      {
        data.paymentMethods.map((pm, i) => {
          return (
            <div key={i} className={styles.paymentMethodItem}>
              <div className={styles.paymentMethodHeader}>
                <div>{pm.method}</div>
                <div>{formatNumber(pm.amount, currency.symbol)} · {numeral(pm.percentage).format('0.0')}%</div>
              </div>
              <div className={styles.progress}>
                <div className={styles.progressBar} style={{width: `${pm.percentage}%`}}></div>
              </div>
            </div>
          )
        })
      }
    </div>
  )
}

export default PosControl